import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { Camelized, camelizeKeys, decamelizeKeys } from 'humps';

import { components } from '@/types/codegen/apiTypes';

import { useAuthStore } from '../stores/authStore'; // Import the store
import { performLogout } from '../utils/logout';
import { API_CONFIG } from './apiConfig';

// NEW: SumSub SDK v2 Types
export interface InitializeSDKResponse {
  success: boolean;
  access_token?: string;
  external_user_id?: string;
  already_verified?: boolean;
  error?: string;
  message?: string;
}

export interface RefreshTokenRequest {
  external_user_id: string;
}

export interface RefreshTokenResponse {
  success: boolean;
  access_token?: string;
  error?: string;
}

// /api/users/me response
export interface UserResponse {
  id: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  date_joined: string;
  last_login?: string | null;
  state?: string | null;
}

// Extended Axios request config with retry flag
interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

// Create the API client
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and anonymous ID
api.interceptors.request.use(
  (config) => {
    // List of endpoints that require API key authentication
    const apiKeyEndpoints = ['/api/users/email/verify/confirm', '/api/auth/password/reset'];

    // Check if this endpoint requires API key
    const requiresApiKey = apiKeyEndpoints.some((endpoint) => config.url?.includes(endpoint));

    // Get token from localStorage
    const token = useAuthStore.getState().token;

    if (requiresApiKey) {
      // For API key endpoints, always use API key
      if (API_CONFIG.API_KEY) {
        config.headers['X-API-Key'] = API_CONFIG.API_KEY;
      }
    } else if (token) {
      // For other endpoints, use token if available
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // If no token and not an API key endpoint, still add API key as fallback
      if (API_CONFIG.API_KEY) {
        config.headers['X-API-Key'] = API_CONFIG.API_KEY;
      }
    }

    // Include anonymous ID in headers for tracking
    const anonymousId = useAuthStore.getState().anonymousId;
    if (anonymousId) {
      config.headers['X-Anonymous-ID'] = anonymousId;
    }

    // Log requests only in development mode
    if (API_CONFIG.IS_DEV) {
      console.debug(`Request to ${config.url}:`, {
        method: config.method,
        hasApiKey: !!config.headers['X-API-Key'],
        hasToken: !!config.headers.Authorization,
      });
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle token refresh on 401 errors
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig;

    // Handle 401 Unauthorized errors
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Get refresh token from store
        const refreshToken = useAuthStore.getState().refreshToken;

        if (refreshToken) {
          // Create a new instance to avoid interceptors
          const refreshResponse = await axios.post<{ access: string }>(`${API_CONFIG.API_BASE_URL}/api/token/refresh`, {
            refresh: refreshToken,
          });

          if (refreshResponse.data.access) {
            // Save the new token
            const newAccessToken = refreshResponse.data.access;
            useAuthStore.setState({ token: newAccessToken });

            // Update the original request with the new token
            if (!originalRequest.headers) {
              originalRequest.headers = {};
            }
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

            return api(originalRequest);
          }
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);

        // Clear all user data if refresh fails
        performLogout();

        // Redirect to login on auth errors
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
      }
    }

    return Promise.reject(error);
  },
);

// Define types from the API schema
export type Registration = {
  id: string;
  user: string;
  // Add other fields as needed
};
export type Address = {
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
};

// Lead generation API
export interface LeadData {
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  email?: string;
  zohoCustomerId?: string;
}

export interface LeadResponse {
  userId?: string;
  registrationId?: string;
  message: string;
  details?: string;
  isNewUser?: boolean;
}

export interface CredentialsEmailData {
  userId: string;
  email: string;
  firstName: string;
}

export interface CredentialsEmailResponse {
  success: boolean;
  message: string;
  emailSent?: boolean;
}

export const customerApi = {
  putSetPassword: async (
    customerData: Camelized<components['schemas']['SetPasswordRequest']>,
  ): Promise<Camelized<components['schemas']['CustomerRegistrationResponse']>> => {
    const { data } = await api.put<components['schemas']['CustomerRegistrationResponse']>(
      '/api/customer-profiles/set-password',
      decamelizeKeys(customerData),
    );
    return camelizeKeys(data) as Camelized<components['schemas']['CustomerRegistrationResponse']>;
  },
  putRegistration: async (
    customerData: Camelized<components['schemas']['CustomerUpdateRequest']>,
  ): Promise<Camelized<components['schemas']['CustomerRegistrationResponse']>> => {
    const { data } = await api.put<components['schemas']['CustomerRegistrationResponse']>(
      '/api/customer-profiles/registration',
      decamelizeKeys(customerData),
    );
    return camelizeKeys(data) as Camelized<components['schemas']['CustomerRegistrationResponse']>;
  },
  getProfile: async (profileId: string): Promise<Camelized<components['schemas']['CustomerProfileResponse']>> => {
    const { data } = await api.get<components['schemas']['CustomerProfileResponse']>(
      `/api/customer-profiles/${profileId}`,
    );
    return camelizeKeys(data) as Camelized<components['schemas']['CustomerProfileResponse']>;
  },
  putEmployment: async (
    customerId: string,
    employmentData: Camelized<components['schemas']['EmploymentUpdateRequest']>,
  ): Promise<Camelized<components['schemas']['EmploymentResponse']>> => {
    const { data } = await api.put<components['schemas']['EmploymentResponse']>(
      `/api/customer-profiles/${customerId}/employment`,
      decamelizeKeys(employmentData),
    );
    return camelizeKeys(data) as Camelized<components['schemas']['EmploymentResponse']>;
  },
  putFinancialInfo: async (
    customerId: string,
    financialInfoData: Camelized<components['schemas']['FinancialInfoRequest']>,
  ): Promise<Camelized<components['schemas']['FinancialInfoResponse']>> => {
    const { data } = await api.put<components['schemas']['FinancialInfoResponse']>(
      `/api/customer-profiles/${customerId}/financial-info`,
      decamelizeKeys(financialInfoData),
    );
    return camelizeKeys(data) as Camelized<components['schemas']['FinancialInfoResponse']>;
  },
  postConsent: async (
    customerId: string,
    consentData: Camelized<components['schemas']['ConsentRequest']>,
  ): Promise<Camelized<components['schemas']['ConsentResponse']>> => {
    const { data } = await api.post<components['schemas']['ConsentResponse']>(
      `/api/customer-profiles/${customerId}/consent`,
      decamelizeKeys(consentData),
    );
    return camelizeKeys(data) as Camelized<components['schemas']['ConsentResponse']>;
  },
  getRegistrationSummary: async (
    profileId: string,
  ): Promise<Camelized<components['schemas']['RegistrationSummaryResponse']>> => {
    const { data } = await api.get<components['schemas']['RegistrationSummaryResponse']>(
      `/api/customer-profiles/${profileId}/registration-summary`,
    );
    return camelizeKeys(data) as Camelized<components['schemas']['RegistrationSummaryResponse']>;
  },
  postSubmitRegistration: async (
    profileId: string,
  ): Promise<Camelized<components['schemas']['SubmitRegistrationResponse']>> => {
    const { data } = await api.post<components['schemas']['SubmitRegistrationResponse']>(
      `/api/customer-profiles/${profileId}/submit-registration`,
    );
    return camelizeKeys(data) as Camelized<components['schemas']['SubmitRegistrationResponse']>;
  },
};

export const userApi = {
  postEmailVerifyConfirm: async (
    token: string,
  ): Promise<Camelized<components['schemas']['EmailVerificationConfirmResponse']>> => {
    const { data } = await api.post<components['schemas']['EmailVerificationConfirmResponse']>(
      '/api/users/email/verify/confirm',
      { token },
    );
    return camelizeKeys(data) as Camelized<components['schemas']['EmailVerificationConfirmResponse']>;
  },
  postEmailVerifySend: async (): Promise<Camelized<components['schemas']['ResendConfirmationResponse']>> => {
    const { data } =
      await api.post<components['schemas']['ResendConfirmationResponse']>('/api/users/email/verify/send');
    return camelizeKeys(data) as Camelized<components['schemas']['ResendConfirmationResponse']>;
  },
  getCurrentUser: async (): Promise<UserResponse> => {
    const { data } = await api.get<UserResponse>('/api/users/me');
    return camelizeKeys(data) as UserResponse;
  },
};

// NEW: SumSub SDK v2 API - Uses the new backend endpoints
export const sumsubSDKApi = {
  /**
   * NEW: Initialize SumSub SDK 2.0 verification
   * Calls: POST /api/sumsub-v2/sdk/initialize
   * Uses JWT authentication to identify user automatically
   */
  initialize: async (): Promise<InitializeSDKResponse> => {
    try {
      console.log('🚀 Initializing SumSub SDK 2.0 verification...');

      const { data } = await api.post<InitializeSDKResponse>('/api/sumsub-v2/sdk/initialize');

      console.log('✅ SDK initialization response:', {
        success: data.success,
        hasToken: !!data.access_token,
        externalUserId: data.external_user_id,
        alreadyVerified: data.already_verified,
      });

      return data;
    } catch (error: any) {
      console.error('❌ Failed to initialize SDK verification:', error);

      return {
        success: false,
        error:
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          'Failed to initialize verification',
      };
    }
  },

  /**
   * NEW: Refresh SumSub SDK access token
   * Calls: POST /api/sumsub-v2/sdk/refresh-token
   * Called when SDK token expires during verification flow
   */
  refreshToken: async (external_user_id: string): Promise<RefreshTokenResponse> => {
    try {
      console.log('🔄 Refreshing SDK access token for user:', external_user_id);

      const requestData: RefreshTokenRequest = { external_user_id };

      const { data } = await api.post<RefreshTokenResponse>('/api/sumsub-v2/sdk/refresh-token', requestData);

      console.log('✅ SDK token refresh successful');

      return data;
    } catch (error: any) {
      console.error('❌ Failed to refresh SDK access token:', error);

      return {
        success: false,
        error:
          error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to refresh token',
      };
    }
  },

  /**
   * NEW: Track verification completion
   * Optional logging for verification events (currently just console logging)
   */
  onComplete: async (externalUserId: string, reviewResult?: any): Promise<void> => {
    console.log('📤 Verification completed:', {
      externalUserId,
      reviewResult: reviewResult
        ? {
            reviewAnswer: reviewResult.reviewAnswer,
            reviewStatus: reviewResult.reviewStatus,
          }
        : null,
    });

    // Future enhancement: Could add completion tracking endpoint
    // await api.post('/api/sumsub-v2/verification-completed', { externalUserId, reviewResult });
  },

  /**
   * NEW: Track status updates
   * Optional logging for status changes (currently just console logging)
   */
  updateStatus: async (externalUserId: string, status: string): Promise<void> => {
    console.log('📋 Status updated:', { externalUserId, status });

    // Future enhancement: Could add status update tracking endpoint
    // await api.post('/api/sumsub-v2/status-update', { externalUserId, status });
  },
};

// Updated applicantsApi with better error handling and deprecated method warnings
export const applicantsApi = {};

export { api };
